import { useMemo } from "react";

import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { LLMSetupBrandInput } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/components/LLMSetupBrandInput/LLMSetupBrandInput";
import { LLMSetupCountrySelector } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/components/LLMSetupCountrySelector";
import { LLMSetupLanguageSelector } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/components/LLMSetupLanguageSelector";
import { LLMSetupSectionHeader } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/components/LLMSetupSectionHeader";
import { useFormStep } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/hooks/useFormStep";
import type { IFormStepContextValue } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/types";
import { DomainsSection } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/components/DomainsSection";
import { ModelSelector } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/components/models-selector/ModelSelector";

import { useFilteredChatbots } from "../../hooks/useFilteredChatbots";

export const LLMSetupFormStep1 = () => {
    const {
        stepData: {
            brandNames,
            domains,
            selectedCountry,
            selectedLanguage,
            selectedModels,
            includeSubdomains,
            trackerConfigurationsData,
            isLoading,
            isDetectingBrands,
        },
        stepHandlers: {
            handleBrandsChange,
            handleDomainsChange,
            handleCountriesChange,
            handleLanguageChange,
            handleModelsChange,
            handleSubdomainsChange,
        },
        stepErrors,
        showStepErrors,
    } = useFormStep() as IFormStepContextValue;

    const { filteredChatbots, isLoading: isConfigLoading } = useFilteredChatbots(selectedCountry);
    const brandError = useMemo<string>(() => {
        if (!showStepErrors) {
            return "";
        }
        return stepErrors.find((err) => err.field === "brand")?.message ?? "";
    }, [stepErrors, showStepErrors]);

    return (
        <>
            <LLMSetupBrandInput
                brandNames={brandNames}
                onBrandsChange={handleBrandsChange}
                error={brandError}
                isLoading={isDetectingBrands}
            />
            <DomainsSection
                domains={domains}
                changeDomains={handleDomainsChange}
                title={translate("llm.setup.domains.title")}
                titleFontVariant="body2"
                subtitle={translate("llm.setup.domains.subtitle")}
                autoFocusLast
                subdomainsProps={{
                    enabled: true,
                    state: includeSubdomains,
                    onChange: handleSubdomainsChange,
                    showTooltip: true,
                }}
            />
            <Flex
                flexDirection="column"
                gap="12px"
            >
                <LLMSetupSectionHeader
                    title={
                        <Text
                            fontVariant="subtitle2"
                            data-automation="llm-setup-locale-title"
                        >
                            {translate("llm.setup.locale.title")}
                        </Text>
                    }
                />
                <Flex flexDirection="column">
                    <Flex
                        flexDirection="column"
                        gap="12px"
                    >
                        <Flex
                            gap="12px"
                            justifyContent="space-between"
                        >
                            <LLMSetupCountrySelector
                                selectedCountry={selectedCountry}
                                onCountriesChange={handleCountriesChange}
                                supportedCountriesByChatbot={trackerConfigurationsData?.supportedCountriesByChatbot}
                                isLoading={isLoading}
                            />
                            <LLMSetupLanguageSelector
                                selectedLanguage={selectedLanguage}
                                onLanguageChange={handleLanguageChange}
                                countryCode={selectedCountry?.code}
                                languagesList={trackerConfigurationsData?.supportedLanguages}
                                isLoading={isLoading}
                            />
                        </Flex>
                        <ModelSelector
                            selectedModels={selectedModels}
                            supportedModels={filteredChatbots}
                            selectedCountry={selectedCountry}
                            isLoading={isLoading || isConfigLoading}
                            onChange={handleModelsChange}
                        />
                    </Flex>
                </Flex>
            </Flex>
        </>
    );
};
